// TabularInvoices.tsx
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { KTSVG } from '../../../_metronic/helpers';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import TableHeading from '../../../components/tableHeading/TableHeading';
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu';
import TextField from '../../../components/textFields/TextField';
import { constraintConfig } from '../../../constraintConfig';
import labelKey from '../../localization/label.json';
import {
    canEditLineItem,
    canMergeLineItem,
    canUnmergeLineItem,
    canViewViolations,
    getAttachedReceiptExpenseId,
    getEffectiveLineItemId,
} from './allReceiptsActionHelpers';
import { formatReceiptNumberDisplay } from '../expenseItems/MergeSelectedLineItemHeader';
import ExpenseStatusComp from '../../../components/StatusComp/ExpenseStatusComp';
import SsRowActions, { SsRowActionItem } from '../../../components/tableActions/SsRowActions';

type FieldType =
    | 'text'
    | 'currency'
    | 'date'
    | 'datetime'
    | 'status'
    | 'source'
    | 'user';

interface ColumnConfig {
    id: string;
    label: string;
    sortable?: boolean;
    className?: string;
    type?: FieldType;
    align?: 'left' | 'center' | 'end';
    render?: (data: any, index?: number) => React.ReactNode;
}

interface TabularInvoicesProps {
    data: any[];
    isLoading: boolean;
    onViewReport?: (expenseId: number) => void;
    onViewLineItem?: (expenseItemId: number) => void;
    onViewUsedLineItem?: (expenseItemId: number) => void;
    onRetry?: (invoiceDataDraftId: number) => void;
    onTriggerExpenseReport?: (expenseId: number) => void;
    onTriggerItemsById?: (expenseId: number) => void;
    onTriggerItemById?: (expenseItemId: number) => void;
    onCompareOcrLineItem?: (dataItem: any) => void;
    onViewOcrText?: (dataItem: any) => void;
    onCopyAttachmentUrl?: (dataItem: any) => void;
    onViewViolations?: (dataItem: any) => void;
    onMergeLineItem?: (dataItem: any) => void;
    onUnmergeLineItem?: (dataItem: any) => void;
    onEditLineItem?: (dataItem: any) => void;
    setShowExpenseItemDetailModal?: (show: boolean) => void;
    setAttachmentDeleteData?: (id: number) => void;
    retryInvoiceHandler?: () => void;
    expenseDetailHandler?: () => void;
    onRowClick?: (data: any) => void;
    selectedRowId?: any;
    onClearFilters?: () => void;
    filterCount?: number;
    onDelete?: (id: number) => void;
    sortBy?: string;
    sortOrder?: string;
    onSort?: (columnId: string) => void;
    showUserColumn?: boolean;
}

const getStatusBadge = (ocrReadStatus: number) => {
    const statusMap: { [key: number]: { label: string; className: string } } = {
        0: { label: labelKey.processing, className: 'badge-light-warning' },
        1: { label: labelKey.readyToUse, className: 'badge-light-success' },
        2: { label: labelKey.failed, className: 'badge-light-danger' },
        3: { label: labelKey.attached, className: 'badge-light-primary' },
        4: { label: labelKey.submitted, className: 'badge-info' },
        5: { label: labelKey.alreadyUsed, className: 'badge-warning' },
        6: { label: labelKey.futureReceipt, className: 'badge-light-info' },
        7: { label: labelKey.cCReceipt, className: 'badge-light-info' },
    };
    const info = statusMap[ocrReadStatus];
    if (!info) return <span className="text-muted">N/A</span>;
    return (
        <span className={`badge ${info.className} fs-7 fw-semibold`}>
            {ocrReadStatus === 0 ? (
                <>
                    {info.label}... <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </>
            ) : (
                info.label
            )}
        </span>
    );
};

const getSourceIcon = (recordSourceID: number) => {
    switch (recordSourceID) {
        case constraintConfig.recordSourceId.WebApp:
            return <KTSVG path="/media/icons/figmaIcons/Web.svg" className='svg-icon-1 ss-source-icon' data-tooltip-id="receipt-source" data-tooltip-content='Website' />;
        case constraintConfig.recordSourceId.Mobile:
            return <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className='svg-icon-1 ss-source-icon' data-tooltip-id="receipt-source" data-tooltip-content='Mobile' />;
        case constraintConfig.recordSourceId.BankFeed:
            return <KTSVG path="/media/icons/figmaIcons/Bank.svg" className='svg-icon-1 ss-source-icon' data-tooltip-id="receipt-source" data-tooltip-content='Bank' />;
        case constraintConfig.recordSourceId.GorillaExpense:
            return <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className='svg-icon-1' data-tooltip-id="receipt-source" data-tooltip-content='Gorilla Expense' />;
        default:
            return <span className="text-muted">N/A</span>;
    }
};

const getSourceLabel = (recordSourceID: number) => {
    switch (recordSourceID) {
        case constraintConfig.recordSourceId.WebApp:
            return 'Website';
        case constraintConfig.recordSourceId.Mobile:
            return 'Mobile';
        case constraintConfig.recordSourceId.BankFeed:
            return 'Bank';
        case constraintConfig.recordSourceId.GorillaExpense:
            return 'Gorilla Expense';
        default:
            return 'N/A';
    }
};

const TabularInvoices: React.FC<TabularInvoicesProps> = ({
    data,
    isLoading,
    onTriggerExpenseReport,
    onTriggerItemsById,
    onTriggerItemById,
    onCompareOcrLineItem,
    onViewOcrText,
    onCopyAttachmentUrl,
    onViewViolations,
    onMergeLineItem,
    onUnmergeLineItem,
    onEditLineItem,
    setShowExpenseItemDetailModal,
    setAttachmentDeleteData,
    retryInvoiceHandler,
    expenseDetailHandler,
    onRowClick,
    selectedRowId,
    onClearFilters,
    filterCount = 0,
    onDelete,
    sortBy = '',
    sortOrder = '',
    onSort,
    showUserColumn = true,
}) => {
    const columns: ColumnConfig[] = [
        ...(showUserColumn
            ? [{
                id: 'orgUserName',
                label: 'User',
                sortable: true,
                className: 'min-w-120px',
                type: 'user' as FieldType,
                align: 'center' as const,
            }]
            : []),
        {
            id: 'ocrReadStatus',
            label: 'Receipt Status',
            sortable: true,
            className: 'min-w-120px',
            type: 'status',
            align: 'center',
        },
        {
            id: 'invoiceNo',
            label: 'Receipt Number',
            sortable: true,
            className: 'min-w-120px',
            align: 'center',
            render: (dataItem) => {
                const raw = dataItem?.invoiceNo ? String(dataItem.invoiceNo) : ''
                if (!raw) return <span className="text-muted">N/A</span>
                const display = formatReceiptNumberDisplay(raw)
                return (
                    <span title={raw !== display ? raw : undefined}>
                        {display}
                    </span>
                )
            },
        },
        {
            id: 'invoiceDate',
            label: 'Receipt Date',
            sortable: true,
            className: 'min-w-120px',
            type: 'date',
            align: 'center',
        },
        {
            id: 'amount',
            label: 'Receipt Amount',
            sortable: true,
            className: 'min-w-110px',
            type: 'currency',
            align: 'end',
        },
        {
            id: 'createdDT',
            label: 'Receipt Uploaded From',
            sortable: true,
            className: 'min-w-140px',
            align: 'center',
            render: (dataItem) => (
                <div className="d-flex flex-column align-items-center gap-1">
                    <span
                        title={getSourceLabel(dataItem?.recordSourceID)}
                    >
                        {getSourceIcon(dataItem?.recordSourceID)}
                    </span>
                    {dataItem?.createdDT ? (
                        <span
                            className="fs-8 fw-semibold text-gray-800"
                            title="Receipt Uploaded Date"
                            data-tooltip-id="receipt-uploaded-date"
                            data-tooltip-content="Receipt Uploaded Date"
                        >
                            <DateTimeComp formattedDate={dataItem.createdDT} />
                        </span>
                    ) : (
                        <span className="fs-8 text-gray-600">—</span>
                    )}
                </div>
            ),
        },
        {
            id: 'vendorName',
            label: 'SS Item ID / Item Category / Item Vendor',
            sortable: true,
            className: 'min-w-180px',
            align: 'left',
            render: (dataItem) => {
                const lineItemId = getEffectiveLineItemId(dataItem);
                return (
                    <div className="d-flex flex-column align-items-start gap-0 text-start lh-sm">
                        {lineItemId ? (
                            <span className="fs-8 text-gray-700" title="Item SS ID">
                                Item #{lineItemId}
                            </span>
                        ) : null}
                        {dataItem?.category ? (
                            <span className="fs-7" title="Category">{dataItem.category}</span>
                        ) : null}
                        <span className="fw-semibold">
                            {dataItem?.itemVendorName || dataItem?.vendorName || 'N/A'}
                        </span>
                    </div>
                );
            },
        },
        {
            id: 'expenseItemDate',
            label: 'Expense Item Date',
            sortable: true,
            className: 'min-w-120px',
            type: 'date',
            align: 'center',
        },
        {
            id: 'expenseItemAmount',
            label: 'Expense Item Amount',
            sortable: true,
            className: 'min-w-110px',
            type: 'currency',
            align: 'end',
        },
        {
            id: 'lineItemRecordSourceId',
            label: 'Item Source',
            sortable: true,
            className: 'min-w-120px',
            align: 'center',
            render: (dataItem) => (
                <div className="d-flex flex-column align-items-center gap-1">
                    {getSourceIcon(dataItem?.lineItemRecordSourceId)}
                    {dataItem?.expenseItemCreatedDate ? (
                        <span
                            className="fs-8 fw-semibold text-gray-800"
                            title="Item Created Date"
                            data-tooltip-id="item-created-date"
                            data-tooltip-content="Item Created Date"
                        >
                            <DateTimeComp formattedDate={dataItem.expenseItemCreatedDate} />
                        </span>
                    ) : (
                        <span className="fs-8 text-gray-600">—</span>
                    )}
                </div>
            ),
        },
        {
            id: 'trackingId',
            label: 'Tracking ID',
            sortable: true,
            className: 'min-w-120px',
            align: 'center',
            render: (dataItem) => {
                const hasReport = !!dataItem?.trackingId && !!dataItem?.expenseId;
                const copyTrackingId = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!dataItem?.trackingId) return;
                    navigator.clipboard
                        .writeText(String(dataItem.trackingId))
                        .then(() => toast.success('Tracking ID copied'))
                        .catch(() => toast.error('Unable to copy Tracking ID'));
                };
                return (
                    <div className="d-flex flex-column align-items-center gap-1">
                        {hasReport ? (
                            <span
                                className="text-primary fw-bold cursor-pointer"
                                title="Click to copy Tracking ID"
                                data-tooltip-id="receipt-actions"
                                data-tooltip-content="Click to copy Tracking ID"
                                onClick={copyTrackingId}
                            >
                                {dataItem.trackingId}
                            </span>
                        ) : (
                            <span className="text-muted">N/A</span>
                        )}
                        {dataItem?.expenseStatus?.id > 0 ? (
                            <div title="Expense Status">
                                <ExpenseStatusComp
                                    expenseStatusId={dataItem.expenseStatus.id}
                                    expenseStatusTitle={dataItem.expenseStatus.title}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            },
        },        
        {
            id: 'actions',
            label: labelKey.actions,
            render: (dataItem) => renderActions(dataItem),
            align: 'end',
        },
    ];

    const [hiddenColumns, setHiddenColumns] = React.useState<string[]>([]);

    const handleToggleColumn = (columnId: string) => {
        setHiddenColumns((prev) =>
            prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
        );
    };

    const openLineItem = (dataItem: any, e: React.MouseEvent) => {
        e.stopPropagation();
        const lineItemId = getEffectiveLineItemId(dataItem);
        if (!lineItemId) {
            toast.warning('No line item is linked to this receipt.');
            return;
        }
        // Parent handler opens the modal after clearing stale query data
        if (onTriggerItemById) onTriggerItemById(lineItemId);
        else if (setShowExpenseItemDetailModal) setShowExpenseItemDetailModal(true);
    };

    const openReport = (dataItem: any, e: React.MouseEvent) => {
        e.stopPropagation();
        const expenseId =
            getAttachedReceiptExpenseId(dataItem) ||
            (Number(dataItem?.expenseId) > 0 ? Number(dataItem.expenseId) : 0);
        if (!expenseId) {
            toast.warning('No expense report is linked to this receipt.');
            return;
        }
        if (expenseDetailHandler) expenseDetailHandler();
        if (onTriggerExpenseReport) onTriggerExpenseReport(expenseId);
        if (onTriggerItemsById) onTriggerItemsById(expenseId);
    };

    const canViewReport = (dataItem: any) =>
        !!getAttachedReceiptExpenseId(dataItem) || (!!dataItem?.trackingId && Number(dataItem?.expenseId) > 0);

    const canViewLineItem = (dataItem: any) => !!getEffectiveLineItemId(dataItem);

    const buildReceiptRowActions = (dataItem: any): SsRowActionItem[] => {
        const lineItemId = getEffectiveLineItemId(dataItem);
        const actions: SsRowActionItem[] = [];

        if (dataItem?.fileURL) {
            actions.push({
                key: 'attachment',
                label: 'Open attachment',
                tooltipId: 'receipt-actions',
                icon: <i className="fas fa-paperclip" />,
                onClick: () => window.open(dataItem.fileURL, '_blank', 'noopener,noreferrer'),
            });
        }

        if (canViewReport(dataItem)) {
            actions.push({
                key: 'view-report',
                label: 'View Report',
                tooltipId: 'receipt-actions',
                tooltipContent:
                    dataItem?.ocrReadStatus === 5 || dataItem?.receiptAtatchedToLineItemID
                        ? 'View Expense Report for the line item this receipt is already attached to'
                        : 'View Report',
                icon: <i className="fas fa-file-alt" />,
                onClick: () => openReport(dataItem, { stopPropagation: () => undefined } as React.MouseEvent),
            });
        }

        if (canViewLineItem(dataItem)) {
            actions.push({
                key: 'view-line-item',
                label: 'View Line Item',
                tooltipId: 'receipt-actions',
                tooltipContent:
                    dataItem?.ocrReadStatus === 5 || dataItem?.receiptAtatchedToLineItemID
                        ? 'View Line item for already attached this receipt'
                        : `View Line Item (#${lineItemId})`,
                icon: <i className="fas fa-list" />,
                onClick: () => openLineItem(dataItem, { stopPropagation: () => undefined } as React.MouseEvent),
            });
        }

        if (canViewLineItem(dataItem) && onCompareOcrLineItem) {
            actions.push({
                key: 'compare-ocr',
                label: 'Compare OCR',
                tooltipId: 'receipt-actions',
                icon: <i className="fas fa-exchange-alt" />,
                onClick: () => onCompareOcrLineItem(dataItem),
            });
        }

        if (dataItem?.fileURL && onCopyAttachmentUrl) {
            actions.push({
                key: 'copy-url',
                label: 'Copy Attachment URL',
                tooltipId: 'receipt-actions',
                icon: <i className="fas fa-link" />,
                onClick: () => onCopyAttachmentUrl(dataItem),
            });
        }

        if (canViewViolations(dataItem) && onViewViolations) {
            actions.push({
                key: 'violations',
                label: 'View Violations',
                tooltipId: 'receipt-actions',
                icon: <i className="fas fa-exclamation-triangle" />,
                onClick: () => onViewViolations(dataItem),
            });
        }

        if (canEditLineItem(dataItem) && onEditLineItem) {
            actions.push({
                key: 'edit-line-item',
                label: 'Edit Line Item',
                tooltipId: 'receipt-actions',
                icon: <KTSVG path="/media/icons/duotune/art/art005.svg" className="svg-icon-3" />,
                onClick: () => onEditLineItem(dataItem),
            });
        }

        if (canMergeLineItem(dataItem) && onMergeLineItem) {
            actions.push({
                key: 'merge',
                label: 'Merge Line Item',
                tooltipId: 'receipt-actions',
                icon: <KTSVG path="/media/icons/duotune/general/merge.svg" className="svg-icon-3" />,
                onClick: () => onMergeLineItem(dataItem),
            });
        }

        if (canUnmergeLineItem(dataItem) && onUnmergeLineItem) {
            actions.push({
                key: 'unmerge',
                label: 'UnMerge Line Item',
                tooltipId: 'receipt-actions',
                icon: <KTSVG path="/media/icons/duotune/general/unmerge.svg" className="svg-icon-3" />,
                onClick: () => onUnmergeLineItem(dataItem),
            });
        }

        if (onViewOcrText) {
            actions.push({
                key: 'ocr-text',
                label: 'View OCR Text',
                tooltipId: 'receipt-actions',
                icon: <i className="fas fa-file-code" />,
                onClick: () => onViewOcrText(dataItem),
            });
        }

        if (dataItem?.ocrReadStatus === 2 && dataItem?.noOfRetry <= 2) {
            actions.push({
                key: 'retry',
                label: `Retry (${dataItem?.noOfRetry})`,
                tooltipId: 'receipt-actions',
                icon: <i className="fas fa-redo" />,
                onClick: () => {
                    if (setAttachmentDeleteData) setAttachmentDeleteData(dataItem?.invoiceDataDraftId);
                    if (retryInvoiceHandler) retryInvoiceHandler();
                },
            });
        }

        if (
            (dataItem?.expenseItemId == null && !dataItem?.usedExpenseItemId) ||
            (dataItem.ocrReadStatus !== 3 && dataItem.ocrReadStatus !== 4)
        ) {
            actions.push({
                key: 'delete',
                label: 'Delete Receipt',
                tooltipId: 'receipt-actions',
                icon: <i className="fa fa-trash" />,
                onClick: () => {
                    if (onDelete) onDelete(dataItem?.invoiceDataDraftId);
                    else if (setAttachmentDeleteData) setAttachmentDeleteData(dataItem?.invoiceDataDraftId);
                },
            });
        }

        return actions;
    };

    const renderActions = (dataItem: any) => (
        <SsRowActions
            menuId={`receipt-row-actions-${dataItem?.invoiceDataDraftId}`}
            actions={buildReceiptRowActions(dataItem)}
        />
    );

    const renderCellContent = (column: ColumnConfig, dataItem: any, index?: number) => {
        if (column.render) return column.render(dataItem, index);

        const value = dataItem?.[column.id];
        if (value === undefined || value === null || value === '') {
            return <span className="text-muted">N/A</span>;
        }

        switch (column.type) {
            case 'user':
                return (
                    <div className="d-flex flex-column align-items-center">
                        <span className="text-primary fw-bold">{dataItem?.orgUserId || ''}</span>
                        <span className="fs-7">{dataItem?.orgUserName || 'N/A'}</span>
                    </div>
                );
            case 'text':
                return <span className={column.align === 'left' ? 'text-start d-block' : ''}>{value}</span>;
            case 'currency':
                return <CurrencyComp amount={value} />;
            case 'date':
            case 'datetime':
                return (
                    <span className="fw-semibold text-gray-800">
                        <DateTimeComp formattedDate={value} />
                    </span>
                );
            case 'status':
                return getStatusBadge(value);
            case 'source':
                return getSourceIcon(value);
            default:
                return <span>{value}</span>;
        }
    };

    const getAlignClass = (column: ColumnConfig) => {
        if (column.align === 'left') return 'text-start';
        if (column.align === 'end') return 'text-end';
        return 'text-center';
    };

    const visibleColumns = columns.filter((col) => !hiddenColumns.includes(col.id));

    const renderNoRecords = () => (
        <tr>
            <td colSpan={visibleColumns.length}>
                <div className="text-center py-8">
                    <h5 className="text-gray-800 fw-bold mb-2">No matching records found</h5>
                    {filterCount > 0 && onClearFilters && (
                        <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={onClearFilters}>
                            Clear All Filters
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );

    const tableScrollRef = useRef<HTMLDivElement>(null)
    const theadRef = useRef<HTMLTableSectionElement>(null)
    const [tooltipEpoch, setTooltipEpoch] = useState(0)

    // Close floating tooltips when the list scrolls so they don't stick off the table
    useEffect(() => {
        let timer: number | undefined
        const hideOpenTooltips = () => {
            document.querySelectorAll<HTMLElement>('.react-tooltip').forEach((tip) => {
                tip.style.visibility = 'hidden'
                tip.style.opacity = '0'
            })
            if (timer !== undefined) window.clearTimeout(timer)
            timer = window.setTimeout(() => setTooltipEpoch((n) => n + 1), 120)
        }
        const tableEl = tableScrollRef.current
        tableEl?.addEventListener('scroll', hideOpenTooltips, { passive: true })
        window.addEventListener('scroll', hideOpenTooltips, { passive: true, capture: true })
        return () => {
            if (timer !== undefined) window.clearTimeout(timer)
            tableEl?.removeEventListener('scroll', hideOpenTooltips)
            window.removeEventListener('scroll', hideOpenTooltips, true)
        }
    }, [])

    return (
        <>
            <Tooltip key={`receipt-actions-${tooltipEpoch}`} id="receipt-actions" place="left" positionStrategy="fixed" />
            <Tooltip key={`receipt-source-${tooltipEpoch}`} id="receipt-source" place="left" positionStrategy="fixed" />
            <Tooltip
                key={`receipt-uploaded-date-${tooltipEpoch}`}
                id="receipt-uploaded-date"
                place="left"
                positionStrategy="fixed"
            />
            <Tooltip
                key={`item-created-date-${tooltipEpoch}`}
                id="item-created-date"
                place="left"
                positionStrategy="fixed"
            />
            <div className='table-responsive table-height' ref={tableScrollRef}>
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 tabular-invoices-table">
                    <thead>
                        <tr className="fw-bold">
                            {visibleColumns.map((column) =>
                                column.id === 'actions' ? (
                                    <th
                                        key={column.id}
                                        className="text-end rounded-end pe-3 border-0 sticky-table-actions ss-user-list-actions-col"
                                    >
                                        <div className="d-flex align-items-center justify-content-end ss-row-actions-header">
                                            <span>{labelKey.actions}</span>
                                        <TableSettingMenu>
                                            {columns
                                                .filter((col) => col.id !== 'actions')
                                                .map((col) => (
                                                    <TextField
                                                        key={col.id}
                                                        rightLabel={col.label}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn(col.id)}
                                                        checked={!hiddenColumns.includes(col.id)}
                                                        fieldClass="mb-4"
                                                    />
                                                ))}
                                        </TableSettingMenu>
                                        </div>
                                    </th>
                                ) : (
                                    <TableHeading
                                        key={column.id}
                                        label={column.label}
                                        sortedColumn={sortBy}
                                        sortOrder={sortOrder}
                                        handleSort={column.sortable && onSort ? onSort : undefined}
                                        columnId={column.id}
                                        className={`${getAlignClass(column)} ${column.className || ''}`}
                                    />
                                )
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {!isLoading && (
                            <>
                                {data?.length > 0 ? (
                                    data.map((dataItem: any, rowIndex: number) => (
                                        <tr
                                            key={dataItem?.invoiceDataDraftId}
                                            className={`cursor-pointer ${
                                                selectedRowId === dataItem?.invoiceDataDraftId
                                                    ? 'tabular-row-selected'
                                                    : ''
                                            }`}
                                            title="Click for view detail Receipt and Line Item details"
                                            onClick={() => onRowClick && onRowClick(dataItem)}
                                        >
                                            {visibleColumns.map((column) => (
                                                <td
                                                    key={column.id}
                                                    className={`${getAlignClass(column)} pe-3${
                                                        column.id === 'actions'
                                                            ? ' text-end sticky-table-actions ss-user-list-actions-col'
                                                            : ''
                                                    }`}
                                                    onClick={
                                                        column.id === 'actions'
                                                            ? (event) => event.stopPropagation()
                                                            : undefined
                                                    }
                                                >
                                                    {renderCellContent(column, dataItem, rowIndex)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    renderNoRecords()
                                )}
                            </>
                        )}
                        {isLoading && (
                            <tr>
                                <td colSpan={visibleColumns.length} className="text-center">
                                    <LoadingComponent />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default TabularInvoices;
